import { useCurrentUser } from './useCurrentUser.ts';
import { useRoles } from './useRoles.tsx';

// Permissions should be configurable in the BE in future...
enum ROLE {
  ADMIN = 'admin',
  TECHNICIAN = 'technician',
  SALES = 'sales',
  CUSTOMER = 'customer',
  STORE_MANAGER = 'store manager',
  AREA_MANAGER = 'area manager',
  SALES_AND_SERVICE_MANAGER = 'sales and service manager',
}

const adminLevelRoles = [ROLE.AREA_MANAGER, ROLE.ADMIN];

// Add any other type of permission needed
const permissions = {
  'authorize.blocked.slot': [...adminLevelRoles],
  'authorize.own.blocked.slot': [
    ...adminLevelRoles,
    ROLE.SALES_AND_SERVICE_MANAGER,
    ROLE.STORE_MANAGER,
  ],
  'is.store.manager': [ROLE.STORE_MANAGER],
  'can.access.settings': [...adminLevelRoles, ROLE.STORE_MANAGER],
  'can.access.rolesAndPermissions': [...adminLevelRoles],
  'add.blocked.slot': [
    ...adminLevelRoles,
    ROLE.SALES_AND_SERVICE_MANAGER,
    ROLE.STORE_MANAGER,
  ],
  'add.edit.location.assets': [...adminLevelRoles, ROLE.STORE_MANAGER],
  'add.edit.location.openingHours': [...adminLevelRoles, ROLE.STORE_MANAGER],
  'add.edit.location': [
    ...adminLevelRoles,
    ROLE.STORE_MANAGER,
    ROLE.SALES_AND_SERVICE_MANAGER,
  ],
  'add.edit.users': [...adminLevelRoles, ROLE.STORE_MANAGER],
  'edit.authorized.blocked.slot': [
    ...adminLevelRoles,
    ROLE.SALES_AND_SERVICE_MANAGER,
  ],
  'edit.unauthorized.blocked.slot': [
    ...adminLevelRoles,
    ROLE.STORE_MANAGER,
    ROLE.SALES_AND_SERVICE_MANAGER,
  ],
  'add.edit.technician': [
    ...adminLevelRoles,
    ROLE.SALES_AND_SERVICE_MANAGER,
    ROLE.STORE_MANAGER,
  ],
  'reschedule.job': [
    ...adminLevelRoles,
    ROLE.SALES_AND_SERVICE_MANAGER,
    ROLE.STORE_MANAGER,
  ],
  'edit.lane': [
    ...adminLevelRoles,
    ROLE.SALES_AND_SERVICE_MANAGER,
    ROLE.STORE_MANAGER,
  ],
  'optimize.jobs': [
    ...adminLevelRoles,
    ROLE.SALES_AND_SERVICE_MANAGER,
    ROLE.STORE_MANAGER,
  ],
  'add.edit.job.types': [...adminLevelRoles, ROLE.SALES_AND_SERVICE_MANAGER],
  'edit.job.type.on.job.builder': [
    ...adminLevelRoles,
    ROLE.SALES_AND_SERVICE_MANAGER,
    ROLE.STORE_MANAGER,
    ROLE.SALES,
    ROLE.CUSTOMER,
    ROLE.TECHNICIAN,
  ],
  'view.scheduling.resource.view': [
    ...adminLevelRoles,
    ROLE.SALES_AND_SERVICE_MANAGER,
    ROLE.STORE_MANAGER,
    ROLE.TECHNICIAN,
  ],
};

export type PermissionName = keyof typeof permissions;

// A more usable abstraction layer for the roles permission system

export const usePermissions = () => {
  const { currentUser } = useCurrentUser();
  const { roles } = useRoles();

  const currentUserRoles: Array<string | ROLE> =
    currentUser && currentUser.roles ? currentUser.roles : [];

  // Filter through existing roles and return an array of strings of matching roles
  const userRoles =
    roles?.results
      .filter((r) => {
        return currentUserRoles.includes(r.id);
      })
      .map((i) => i.name.toLowerCase()) || [];

  const hasPermissions = (permissionName: PermissionName) => {
    return userRoles.some((userRole) =>
      permissions[permissionName].some((permissionName) =>
        userRole.includes(permissionName)
      )
    );
  };

  return { hasPermissions };
};
